/**
 * Hook reutilizable para funcionalidades del chat
 * - Copiar/pegar en el input
 * - Reproducir audio al hacer click en burbujas
 */

import { useCallback, useRef } from 'react';
import { ChatMessage } from '../types';
import { webSocketService } from '../services/webSocketService';

interface UseChatFeaturesOptions {
  /**
   * Habilitar copiar/pegar en el input
   * Útil para modo estudio donde el usuario puede copiar texto de otras fuentes
   */
  enableCopyPaste?: boolean;
  
  /**
   * Habilitar reproducir audio al hacer click en burbujas
   * Útil para todos los modos donde hay respuestas de audio
   */
  enableAudioReplay?: boolean;
  
  /**
   * Modo actual de práctica
   * Algunas funcionalidades pueden variar según el modo
   */
  mode?: string;
}

interface UseChatFeaturesReturn {
  /**
   * Handler para eventos de pegar en el input
   * Extrae texto del clipboard y lo inserta en el input
   */
  handlePaste: (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>, setInputText: (text: string) => void) => void;
  
  /**
   * Handler para click en burbujas de mensajes
   * Reproduce el audio asociado al mensaje si está disponible
   */
  handleMessageClick: (message: ChatMessage) => void;
  
  /**
   * Handler para copiar texto de un mensaje
   * Copia el contenido del mensaje al clipboard
   */
  handleCopyMessage: (message: ChatMessage) => void;
}

/**
 * Hook reutilizable para funcionalidades del chat
 * 
 * @param options - Opciones de configuración
 * @returns Funciones y handlers para usar en componentes de chat
 */
export function useChatFeatures(options: UseChatFeaturesOptions = {}): UseChatFeaturesReturn {
  const {
    enableCopyPaste = true,
    enableAudioReplay = true,
    // mode - reservado para futuras funcionalidades específicas por modo
  } = options;
  
  // Referencia para el audio actual que se está reproduciendo
  const currentReplayAudioRef = useRef<HTMLAudioElement | null>(null);
  // Token para cancelar la secuencia de chunks en curso (tap-replay multi-chunk)
  const replayCancelRef = useRef<{ cancelled: boolean } | null>(null);
  
  /**
   * Manejar evento de pegar en el input
   * Extrae texto del clipboard y lo inserta en el input
   */
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>, setInputText: (text: string) => void) => {
    if (!enableCopyPaste) {
      return; // No hacer nada si está deshabilitado
    }
    
    // Permitir pegar texto desde el clipboard
    const pastedText = e.clipboardData.getData('text/plain');
    
    if (pastedText) {
      e.preventDefault(); // Prevenir el comportamiento por defecto
      
      // Insertar el texto pegado en el input
      // Obtener el input del evento
      const input = e.currentTarget;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;
      
      // Insertar texto en la posición del cursor
      const newValue = currentValue.substring(0, start) + pastedText + currentValue.substring(end);
      setInputText(newValue);
      
      // Restaurar posición del cursor después del texto pegado
      setTimeout(() => {
        const newCursorPos = start + pastedText.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }, 0);
      
    }
  }, [enableCopyPaste]);
  
  /**
   * Manejar click en burbujas de mensajes para reproducir audio.
   * Si la burbuja consolidada tiene varios chunks de audio (audioUrls), los
   * reproduce en secuencia. Cancelable: un nuevo click cancela el playback en curso.
   */
  const handleMessageClick = useCallback((message: ChatMessage) => {
    if (!enableAudioReplay) {
      return;
    }
    if (message.role !== 'assistant') {
      return;
    }

    // Detener audio manual anterior (corta el chain via la bandera cancelled)
    if (currentReplayAudioRef.current) {
      currentReplayAudioRef.current.pause();
      currentReplayAudioRef.current = null;
    }
    if (replayCancelRef.current) {
      replayCancelRef.current.cancelled = true;
    }

    // Construir la lista de URLs a reproducir en secuencia.
    // audioUrls es la fuente preferida (todos los chunks); audioUrl es fallback.
    const urls: string[] = [];
    if (message.audioUrls && message.audioUrls.length > 0) {
      urls.push(...message.audioUrls);
    } else if (message.audioUrl) {
      urls.push(message.audioUrl);
    }
    if (urls.length === 0) {
      return;
    }

    // Detener audio automático si está reproduciendo y notificar
    webSocketService.stopAutomaticAudio();
    webSocketService.setManualAudioPlaying(true);

    const cancelToken = { cancelled: false };
    replayCancelRef.current = cancelToken;
    const recreatedUrls: string[] = []; // tracking para revocar al final

    const cleanup = () => {
      currentReplayAudioRef.current = null;
      webSocketService.setManualAudioPlaying(false);
      for (const u of recreatedUrls) {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      }
      recreatedUrls.length = 0;
      if (replayCancelRef.current === cancelToken) {
        replayCancelRef.current = null;
      }
    };

    const playAt = (idx: number) => {
      if (cancelToken.cancelled) {
        cleanup();
        return;
      }
      if (idx >= urls.length) {
        cleanup();
        return;
      }
      let url = urls[idx];
      const recreated = webSocketService.recreateAudioUrl(url);
      if (recreated && recreated !== url) {
        recreatedUrls.push(recreated);
        url = recreated;
      }
      const audio = new Audio(url);
      currentReplayAudioRef.current = audio;
      audio.onended = () => {
        if (cancelToken.cancelled) {
          cleanup();
          return;
        }
        playAt(idx + 1);
      };
      audio.onerror = (error) => {
        console.error('[ChatFeatures] Error reproduciendo chunk', idx, 'de', urls.length, error);
        // Intentar siguiente para no atascarse en un blob corrupto
        playAt(idx + 1);
      };
      audio.play().catch((error) => {
        console.error('[ChatFeatures] No pudo iniciar reproduccion chunk', idx, error);
        playAt(idx + 1);
      });
    };

    playAt(0);
  }, [enableAudioReplay]);
  
  /**
   * Copiar texto de un mensaje al clipboard
   */
  const handleCopyMessage = useCallback((message: ChatMessage) => {
    if (!message.content) {
      return;
    }
    
    navigator.clipboard.writeText(message.content).then(() => {
      // Opcional: mostrar notificación visual
    }).catch((error) => {
      console.error('[ChatFeatures] Error al copiar texto:', error);
    });
  }, []);
  
  return {
    handlePaste,
    handleMessageClick,
    handleCopyMessage,
  };
}

